<template>
  <a-drawer
    width="35%"
    :label-col="4"
    :wrapper-col="14"
    :visible="open"
    @close="onClose"
    :destroyOnClose="true">
    <a-divider orientation="left">
      <b>{{ formTitle }}</b>
    </a-divider>
    <a-form-model ref="form" :model="form" :rules="rules">
      <a-form-model-item :label="$t('views.systemDept.form.parentDept')" prop="parentId">
        <a-tree-select
          v-model="form.parentId"
          style="width: 100%"
          :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
          :tree-data="deptOptions"
          :placeholder="$t('views.systemDept.form.placeholderSelect')"
          :replaceFields="{children:'children', title:'deptName', key:'id', value: 'id' }"
          tree-default-expand-all
        >
        </a-tree-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemDept.form.deptName')" prop="deptName">
        <a-input v-model="form.deptName" :placeholder="$t('views.systemDept.form.placeholderInput')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemDept.form.orderNum')" prop="orderNum">
        <a-input-number v-model="form.orderNum" :min="0" style="width: 100%"/>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemDept.form.leader')" prop="leader">
        <a-input v-model="form.leader" :placeholder="$t('views.systemDept.form.placeholderInput')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemDept.form.phone')" prop="phone">
        <a-input v-model="form.phone" :placeholder="$t('views.systemDept.form.placeholderInput')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemDept.form.email')" prop="email">
        <a-input v-model="form.email" :placeholder="$t('views.systemDept.form.placeholderInput')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemDept.form.status')" prop="status">
        <a-radio-group v-model="form.status" button-style="solid">
          <a-radio-button v-for="(d, index) in localizedStatusOptions" :key="index" :value="parseInt(d.value)">{{ d.label }}</a-radio-button>
        </a-radio-group>
      </a-form-model-item>
      <div class="bottom-control">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="submitForm">
            {{ $t('views.systemDept.form.save') }}
          </a-button>
          <a-button type="dashed" @click="cancel">
            {{ $t('views.systemDept.form.cancel') }}
          </a-button>
        </a-space>
      </div>
    </a-form-model>
  </a-drawer>
</template>

<script>

import { getDept, addDept, updateDept } from '@/api/system/dept'

export default {
  name: 'CreateForm',
  props: {
    statusOptions: {
      type: Array,
      required: true
    },
    deptOptions: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      submitLoading: false,
      formTitle: '',
      form: {
        id: undefined,
        parentId: undefined,
        deptName: undefined,
        orderNum: 0,
        leader: undefined,
        phone: undefined,
        email: undefined,
        status: 1
      },
      open: false
    }
  },
  computed: {
    localizedStatusOptions () {
      return this.localizeDictOptions(this.statusOptions, 'views.systemDept.dict.status')
    },
    rules () {
      return {
        parentId: [{ required: true, message: this.$t('views.systemDept.form.rules.parentDeptRequired'), trigger: 'blur' }],
        deptName: [{ required: true, message: this.$t('views.systemDept.form.rules.deptNameRequired'), trigger: 'blur' }],
        orderNum: [{ required: true, message: this.$t('views.systemDept.form.rules.orderNumRequired'), trigger: 'blur' }],
        email: [
          {
            type: 'email',
            message: this.$t('views.systemDept.form.rules.emailInvalid'),
            trigger: ['blur', 'change']
          }
        ],
        phone: [
          {
            pattern: /^1[3|4|5|6|7|8|9][0-9]\d{8}$/,
            message: this.$t('views.systemDept.form.rules.phoneInvalid'),
            trigger: 'blur'
          }
        ]
      }
    }
  },
  methods: {
    localizeDictOptions (options, keyPrefix) {
      return (options || []).map(item => {
        const i18nKey = `${keyPrefix}.${item.value}`
        return {
          ...item,
          label: this.$te(i18nKey) ? this.$t(i18nKey) : item.label
        }
      })
    },
    onClose () {
      this.open = false
    },
    cancel () {
      this.open = false
      this.reset()
    },
    reset () {
      this.form = {
        id: undefined,
        parentId: undefined,
        deptName: undefined,
        orderNum: 0,
        leader: undefined,
        phone: undefined,
        email: undefined,
        status: 1
      }
    },
    handleAdd (row) {
      this.reset()
      if (row !== undefined) {
        this.form.parentId = row.id
      }
      this.open = true
      this.formTitle = this.$t('views.systemDept.form.titleAdd')
      this.$emit('select-tree')
    },
    handleUpdate (row) {
      this.reset()
      const deptId = row.id
      getDept(deptId).then(response => {
        this.form = response.data
        this.open = true
        this.formTitle = this.$t('views.systemDept.form.titleEdit')
      })
      this.$emit('select-tree', row)
    },
    submitForm: function () {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.submitLoading = true
          if (this.form.id !== undefined) {
            updateDept(this.form).then(() => {
              this.$message.success(this.$t('views.systemDept.msg.updateSuccess'), 3)
              this.open = false
              this.$emit('ok')
            }).finally(() => {
              this.submitLoading = false
            })
          } else {
            addDept(this.form).then(() => {
              this.$message.success(this.$t('views.systemDept.msg.addSuccess'), 3)
              this.open = false
              this.$emit('ok')
            }).finally(() => {
              this.submitLoading = false
            })
          }
        } else {
          return false
        }
      })
    }
  }
}
</script>
